import React, { ChangeEvent, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import {
  IApiAccountType,
  IApiSelfTradingAccountTypeViewModel,
  IApiUserTradingAccountType,
} from '@api/interfaces/data-contracts';
import { AccountCreateFormWrapper } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateFormWrapper';
import {
  getAvailableTypeTabs,
  getAvailableAccountTypeButtons,
} from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateTabs/utils';
import { AccountCreateToggleButtons } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateToggleButtons/AccountCreateToggleButtons';
import { AccountCreateToggleButtonType } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateToggleButtons/constants';
import { AccountCreateContext } from '@components-member/TradingAccounts/AccountCreate/context';
import { ACCOUNT_CREATE_STEP } from '@components-member/TradingAccounts/AccountCreate/interfaces';
import { TabList, TabListItem, TabPanel, Tabs } from '@components-shared/tabs';
import { keyMap } from '@locales/lokalise/keyMap';
import { reactRouterService } from '@utils/library/reactRouter.service';
import { QUERY_PARAMS, TRADING_ACCOUNT_FORM_TYPE, TRADING_ACCOUNT_TYPE as TRADING_ACCOUNT_TAB } from 'global.enums';
import { observer } from 'mobx-react';

interface IAccountCreateTypeTabs {
  openAccountType: TRADING_ACCOUNT_FORM_TYPE;
  changeStep: (step: ACCOUNT_CREATE_STEP) => void;
  isBackButtonDisplayed: boolean;
  closeModalHandler: () => void;
}

export type TradingAccountTypes = {
  [TRADING_ACCOUNT_FORM_TYPE.SELF]: IApiSelfTradingAccountTypeViewModel[];
  [TRADING_ACCOUNT_FORM_TYPE.MASTER]: IApiUserTradingAccountType[];
  [TRADING_ACCOUNT_FORM_TYPE.INVESTOR]: IApiUserTradingAccountType[];
};

export const AccountCreateTabs = observer((props: IAccountCreateTypeTabs) => {
  const { openAccountType, changeStep, isBackButtonDisplayed, closeModalHandler } = props;

  const {
    accountCreateState: { selfTradingTypes, investorTrading, masterTrading },
    selectedPlatformType,
    contestId,
  } = useContext(AccountCreateContext);

  const [availableTabTypes, setAvailableTabTypes] = useState<Set<IApiAccountType>>(new Set<IApiAccountType>());
  const [availableButtonTypes, setAvailableButtonTypes] = useState<Set<AccountCreateToggleButtonType>>(new Set([]));

  const tradingAccounts: TradingAccountTypes = useMemo(
    () => ({
      [TRADING_ACCOUNT_FORM_TYPE.SELF]: selfTradingTypes.filter((t) => t.tradingPlatformType === selectedPlatformType),
      [TRADING_ACCOUNT_FORM_TYPE.MASTER]: masterTrading.filter((t) => t.tradingPlatformType === selectedPlatformType),
      [TRADING_ACCOUNT_FORM_TYPE.INVESTOR]: investorTrading.filter(
        (t) => t.tradingPlatformType === selectedPlatformType,
      ),
    }),
    [investorTrading, masterTrading, selectedPlatformType, selfTradingTypes],
  );

  useLayoutEffect(() => {
    const availableAccountTypes = getAvailableTypeTabs(tradingAccounts, selectedPlatformType, contestId);
    const availableAccountFormButtonTypes = getAvailableAccountTypeButtons(tradingAccounts, selectedPlatformType);

    setAvailableTabTypes((prev) => new Set([...prev, ...availableAccountTypes]));

    setAvailableButtonTypes((prev) => new Set([...prev, ...availableAccountFormButtonTypes]));
  }, [tradingAccounts, selectedPlatformType, contestId]);

  const onRealTabClick = () =>
    reactRouterService.addUrlQueryParams({ [QUERY_PARAMS.OPEN_ACCOUNT]: TRADING_ACCOUNT_TAB.REAL });
  const onDemoTabClick = () =>
    reactRouterService.addUrlQueryParams({ [QUERY_PARAMS.OPEN_ACCOUNT]: TRADING_ACCOUNT_TAB.DEMO });

  const hasRealTab = availableTabTypes.has(IApiAccountType.Real);
  const hasDemoTab = availableTabTypes.has(IApiAccountType.Demo);

  const tabListItem = [
    {
      isEnable: hasRealTab,
      onClick: onRealTabClick,
      title: keyMap.tradingAccountsRealTitle,
      accountType: openAccountType,
    },
    {
      isEnable: hasDemoTab,
      onClick: onDemoTabClick,
      title: keyMap.tradingAccountsDemoTitle,
      accountType: TRADING_ACCOUNT_FORM_TYPE.DEMO,
    },
  ];

  const onChangeToggleButtons = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    reactRouterService.addUrlQueryParams({ [QUERY_PARAMS.OPEN_ACCOUNT]: e.target.value });
  }, []);

  return (
    <Tabs className="horizontal--tabs">
      <TabList className="tabsWithoutBorder">
        {tabListItem.map(
          (listItem, idx) =>
            listItem.isEnable && <TabListItem key={idx} onClick={listItem.onClick} title={listItem.title} />,
        )}
      </TabList>
      <TabPanel>
        {tabListItem.map(
          (listItem, idx) =>
            listItem.isEnable && (
              <React.Fragment key={idx}>
                {listItem.accountType !== TRADING_ACCOUNT_FORM_TYPE.DEMO && (
                  <AccountCreateToggleButtons
                    availableToggleButtonTypes={availableButtonTypes}
                    selectedAccountType={openAccountType}
                    onChange={onChangeToggleButtons}
                  />
                )}
                <AccountCreateFormWrapper
                  closeModalHandler={closeModalHandler}
                  accountType={listItem.accountType}
                  changeStep={changeStep}
                  isBackButtonDisplayed={isBackButtonDisplayed}
                />
              </React.Fragment>
            ),
        )}
      </TabPanel>
    </Tabs>
  );
});
